import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "slide", "indicator"]

  connect() {
    this.currentPage = 1;
    // Show 3 slides with partial visibility of prev/next on desktop, 1 on mobile
    
    this.slidesPerPage = window.innerWidth < 1000 ? 1 : 3;
    // We want to see 5 slides total (3 main + prev & next) on desktop, 1 on mobile
    this.visibleSlides = window.innerWidth < 1000 ? 1 : 5;
    // Always move exactly one slide at a time
    this.slidesToMove = 1;
    // Increase drag threshold for better full-width navigation
    this.dragThreshold = 50;
    
    // Calculate total pages based on total slides and visible slides per page
    this.totalPages = Math.max(0, this.slideTargets.length - this.slidesPerPage) + 1;
    
    // Make sure we remove justify-center immediately to prevent flickering
    this.containerTarget.classList.remove('justify-center');
    
    // Initialize drag variables (only once)
    this.isDragging = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    
    // Add window resize handler
    window.addEventListener('resize', this.handleResize.bind(this));
    
    // Wait a short moment for images to load and dimensions to be calculated
    setTimeout(() => {
      // Initialize view without animation
      this.updateView(false);
    }, 100);
    
    // Bind event handlers
    this.resizeHandler = this.handleResize.bind(this);
    this.dragStartHandler = this.dragStart.bind(this);
    this.dragMoveHandler = this.dragMove.bind(this);
    this.dragEndHandler = this.dragEnd.bind(this);
    
    // Setup event listeners
    window.addEventListener('resize', this.resizeHandler);
    this.setupDragListeners();
    
    this.updateView();
  }

  disconnect() {
    window.removeEventListener('resize', this.resizeHandler);
    this.removeDragListeners();
  }
  
  setupDragListeners() {
    // Mouse events
    this.containerTarget.addEventListener('mousedown', this.dragStartHandler);
    window.addEventListener('mousemove', this.dragMoveHandler);
    window.addEventListener('mouseup', this.dragEndHandler);
    
    // Touch events for mobile
    this.containerTarget.addEventListener('touchstart', this.dragStartHandler);
    window.addEventListener('touchmove', this.dragMoveHandler);
    window.addEventListener('touchend', this.dragEndHandler);
    
    // Prevent context menu during drag
    this.containerTarget.addEventListener('contextmenu', e => {
      if (this.isDragging) e.preventDefault();
    });
  }
  
  removeDragListeners() {
    // Mouse events
    this.containerTarget.removeEventListener('mousedown', this.dragStartHandler);
    window.removeEventListener('mousemove', this.dragMoveHandler);
    window.removeEventListener('mouseup', this.dragEndHandler);
    
    // Touch events
    this.containerTarget.removeEventListener('touchstart', this.dragStartHandler);
    window.removeEventListener('touchmove', this.dragMoveHandler);
    window.removeEventListener('touchend', this.dragEndHandler);
    
    this.containerTarget.removeEventListener('contextmenu', e => {
      if (this.isDragging) e.preventDefault();
    });
  }
  
  dragStart(e) {
    // Get the proper event for both mouse and touch
    const eventX = e.type.includes('mouse') 
      ? e.clientX 
      : e.touches[0].clientX;
      
    // Start the drag operation
    this.isDragging = true;
    this.startX = eventX;
    this.containerTarget.style.cursor = 'grabbing';
    
    // Get current transform value
    const style = window.getComputedStyle(this.containerTarget);
    const transform = style.getPropertyValue('transform');
    
    // Parse current transform to get translateX value
    let matrix = transform.match(/matrix.*\((.*)\)/);
    if (matrix) {
      matrix = matrix[1].split(', ');
      this.prevTranslate = parseFloat(matrix[4]);
    } else {
      this.prevTranslate = 0;
    }
    
    // Disable transition for smooth dragging
    this.containerTarget.style.transition = 'none';
    
    // Prevent default behavior (e.g., image dragging)
    if (e.type.includes('mouse')) {
      e.preventDefault();
    }
  }
  
  dragMove(event) {
    if (!this.isDragging) return;
    
    // Prevent scrolling on mobile
    if (event.type.includes('touch')) {
      event.preventDefault();
    }
    
    // Get the proper event for both mouse and touch
    const currentX = event.type.includes('mouse')
      ? event.clientX
      : event.touches[0].clientX;
    
    // Calculate how far we've moved
    const diff = currentX - this.startX;
    
    // Calculate new position based on the difference
    this.currentTranslate = this.prevTranslate + diff;
    
    // Apply the movement with transform - using a more responsive approach for full-width
    this.containerTarget.style.transform = `translateX(${this.currentTranslate}px)`;
  }
  
  dragEnd(event) {
    if (!this.isDragging) return;
    
    // End the drag
    this.isDragging = false;
    
    // Reset cursor
    this.containerTarget.style.cursor = '';
    
    // Get the proper event for both mouse and touch
    const eventX = event.type.includes('mouse')
      ? event.clientX
      : event.changedTouches ? event.changedTouches[0].clientX : this.startX;
    
    // Calculate total drag distance
    const diff = eventX - this.startX;
    const absDiff = Math.abs(diff);
    
    // Re-enable transition for smooth sliding
    this.containerTarget.style.transition = 'transform 300ms ease';
    
    // If dragged far enough, change the page
    if (absDiff > this.dragThreshold) {
      if (diff > 0 && this.currentPage > 1) {
        // Previous slide
        this.currentPage--;
        this.updateView(true);
      } else if (diff < 0 && this.currentPage < this.totalPages) {
        // Next slide
        this.currentPage++;
        this.updateView(true);
      } else {
        // If we can't change page (e.g., at the first or last page), snap back
        this.updateView(true);
      }
    } else {
      // Not dragged far enough, snap back to current page
      this.updateView(true);
    }
  }

  handleResize() {
    const newSlidesPerPage = window.innerWidth < 1000 ? 1 : 3;
    const newVisibleSlides = window.innerWidth < 1000 ? 1 : 5;
    // Always move 1 slide at a time regardless of screen size
    const newSlidesToMove = 1;
    
    if (this.slidesPerPage !== newSlidesPerPage) {
      this.slidesPerPage = newSlidesPerPage;
      this.visibleSlides = newVisibleSlides;
      this.slidesToMove = newSlidesToMove;
      this.totalPages = Math.max(0, this.slideTargets.length - newSlidesPerPage) + 1;
      
      // Ensure currentPage is valid after resize
      if (this.currentPage >= this.totalPages) {
        this.currentPage = this.totalPages - 1;
      }
      
      this.updateView(true);
    }
  }

  next() {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.updateView(true);
    }
  }

  prev() {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.updateView(true);
    }
  }

  goToPage(event) {
    const clickedIndex = parseInt(event.currentTarget.dataset.index);
    if (clickedIndex !== this.currentPage && clickedIndex >= 0 && clickedIndex < this.totalPages) {
      this.currentPage = clickedIndex;
      this.updateView(true);
    }
  }

  updateView(animate = false) {
    // Enable/disable transition based on whether we're animating
    if (animate) {
      this.containerTarget.style.transition = 'transform 0.3s ease-out';
    } else {
      this.containerTarget.style.transition = 'none';
    }
    
    // Calculate slide width and account for margin (2px on each side)
    const slideWidth = this.slideTargets[0].offsetWidth;
    const slideMargin = 4; // 2px margin on each side (mx-2)
    
    // Get container dimensions
    const containerWidth = this.containerTarget.parentElement.offsetWidth;
    
    // Calculate the width of each slide unit including margin
    const slideUnitWidth = slideWidth + slideMargin;
    
    // First, remove justify-center from our container since we'll position with transforms
    this.containerTarget.classList.remove('justify-center');
    
    // Calculate the container and viewport widths
    const containerParentWidth = containerWidth;
    const allSlidesWidth = this.slideTargets.length * (slideWidth + slideMargin);
    
    // Different translation calculation based on screen width
    let translateX = 0;
    const isMobile = window.innerWidth < 1000;
    
    // For mobile view (only 1 slide visible at a time)
    if (isMobile) {
      // Get current slide index (0-based)
      const slideIndex = this.currentPage - 1;
      
      // Use direct DOM measurements
      const viewportWidth = this.containerTarget.parentElement.offsetWidth;
      
      // For first slide, center it directly
      if (slideIndex === 0) {
        // Center the slide in the viewport
        translateX = (viewportWidth - slideWidth) / 2;
      } else {
        // For subsequent slides, we need to move left by their position
        // First calculate where slide 0 would be centered
        const slide0Center = (viewportWidth - slideWidth) / 2;
        
        // Then shift left by the number of slides we've moved
        // Each slide is shifted by its full width plus margin
        translateX = slide0Center - (slideIndex * (slideWidth + slideMargin));
      }
      
      console.log('Mobile centering fixed:', { 
        viewportWidth, 
        slideWidth,
        slideIndex,
        translateX
      });
    } else {
      // For desktop (3 slides visible + partial), use simple calculation
      // On desktop, slide 1 stays at left edge, others move by slideUnitWidth
      if (this.currentPage > 1) {
        translateX = -(this.currentPage - 1) * slideUnitWidth;
      }
    }
    
    // Log values for debugging
    console.log('Transform:', { 
      currentPage: this.currentPage, 
      isMobile, 
      slideWidth, 
      slideMargin,
      translateX 
    });
    
    // Apply the transform for positioning
    this.containerTarget.style.transform = `translateX(${translateX}px)`;
    
    // Set explicit width on container to fit all slides and prevent wrapping
    const totalContentWidth = isMobile ? slideUnitWidth : this.slideTargets.length * slideUnitWidth;
    this.containerTarget.style.width = `${totalContentWidth}px`;
    
    // Ensure the main carousel container doesn't overflow
    const mainCarousel = this.element;
    if (mainCarousel) {
      mainCarousel.style.maxWidth = '100%';
    }
    
    // Update indicators if present (removed since we don't use them anymore)
    
    // Use the visibility method instead of custom styling for each slide
    this.updateSlidesVisibility(this.currentPage - 1);

  }
  
  // Add a method to update slides visibility for better performance and consistent display
  updateSlidesVisibility(currentIndex) {
    // For full width carousel, we'll show the current slides and adjacent ones
    const visibleRange = Math.max(2, Math.floor(this.visibleSlides / 2))
    
    this.slideTargets.forEach((slide, index) => {
      const distance = Math.abs(index - currentIndex)
      
      // Center slides are fully visible
      if (distance < this.slidesPerPage) {
        slide.style.opacity = '1'
        slide.style.transform = 'scale(1)'
        slide.style.zIndex = '10'
        slide.style.visibility = 'visible'
        slide.style.pointerEvents = 'auto'
      } 
      // Slides within visible range but not in center are partially visible
      else if (distance <= visibleRange) {
        // Partial preview slides - slightly transparent but not scaled down
        slide.style.opacity = 0.85 - ((distance - this.slidesPerPage + 1) * 0.1)
        slide.style.transform = 'scale(1)' // No scaling to prevent shrinking
        slide.style.zIndex = '5'
        slide.style.visibility = 'visible'
        slide.style.pointerEvents = 'auto'
      } 
      // Slides outside visible range
      else {
        slide.style.opacity = '0.3'
        slide.style.transform = 'scale(1)'
        slide.style.zIndex = '1'
        slide.style.visibility = 'visible' // Keep visible for full-width effect
        slide.style.pointerEvents = 'none'
      }
    })
  }
}
